import { useState, useEffect, useRef } from 'react'
import { Link, NavLink } from 'react-router'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import LanguageSelector from './LanguageSelector'

// Símbolo compacto (o mesmo do favicon) — entra no lugar do logótipo ao fazer scroll
const logoCompacto = '/assets/images/Favicon_APAV.svg'

// A partir de quantos px de scroll o logótipo dá lugar ao símbolo compacto
// (padrão apav.pt / precisodeajuda / discriminacao)
const SCROLL_THRESHOLD = 60

// Os rótulos vivem no catálogo (navbar.links.*); os slugs são rota, não texto,
// e são iguais em todas as línguas — é isso que permite ao seletor de língua
// trocar de idioma mantendo a página atual.
const NAV_LINKS = [
    { key: 'home', href: '/' },
    { key: 'migrantes', href: '/pessoas-migrantes' },
    { key: 'turistas', href: '/pessoas-turistas' },
    { key: 'consequencias', href: '/consequencias-e-impacto' },
    { key: 'direitos', href: '/direitos' },
    { key: 'infoUteis', href: '/informacoes-uteis' },
    { key: 'glossario', href: '/glossario' },
    { key: 'apoio', href: '/apoio-apav', pill: true },
]

export default function Navbar() {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [animationsPaused, setAnimationsPaused] = useState(false)
    const [scrolled, setScrolled] = useState(
        typeof window !== 'undefined' && window.scrollY > SCROLL_THRESHOLD
    )
    // Os rótulos mudam de comprimento com a língua (alemão e russo são ~20%
    // mais longos que o português) e não há breakpoint que sirva às 15. Em vez
    // de adivinhar por língua, mede-se: se logótipo + links não cabem na barra,
    // passa-se ao menu hambúrguer também em desktop. O bloco dos links fica
    // sempre no DOM (invisível e fora do fluxo quando não cabe) para a medida
    // continuar a existir e não oscilar.
    const rowRef = useRef(null)
    const linksRef = useRef(null)
    const [naoCabe, setNaoCabe] = useState(false)

    useEffect(() => {
        const medir = () => {
            const row = rowRef.current
            const links = linksRef.current
            if (!row || !links) return
            const logo = row.firstElementChild
            const folga = 24 // ms-6 entre o logótipo e os links
            setNaoCabe(logo.getBoundingClientRect().width + folga + links.scrollWidth > row.clientWidth)
        }
        medir()
        window.addEventListener('resize', medir)
        // as fontes web chegam depois do primeiro render e alargam os rótulos
        document.fonts?.ready.then(medir)
        // o painel de acessibilidade muda o tamanho do texto no <html> (112%,
        // 125%) — os rótulos alargam sem que a janela mude de tamanho
        const observer = new MutationObserver(medir)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
        return () => {
            window.removeEventListener('resize', medir)
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD)
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    /* Respeitar "Pausar animações" (widget de acessibilidade) e prefers-reduced-motion */
    useEffect(() => {
        const checkMotion = () => {
            const isPaused = document.documentElement.classList.contains('a11y-pause-animations')
            const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            setAnimationsPaused(isPaused || prefersReduced)
        }
        checkMotion()
        const observer = new MutationObserver(checkMotion)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    const transitionProps = (duration = 0.3) =>
        animationsPaused ? { duration: 0 } : { duration, ease: 'easeOut' }

    const navLinks = NAV_LINKS.map((l) => ({ ...l, name: t(`navbar.links.${l.key}`) }))

    return (
        <nav
            role="navigation"
            aria-label={t('navbar.aria')}
            className={`fixed w-full top-0 z-50 transition-all duration-500 ${scrolled
                    ? 'bg-white/95 backdrop-blur-xl shadow-none'
                    : 'bg-white/95 backdrop-blur-xl'
                }`}
        >
            {/* Em xl (1280–1535px) o padding lateral encolhe para caberem 7 links +
                pílula + seletor em línguas de rótulos longos (alemão, russo); em 2xl
                volta ao px-16 para o logótipo alinhar com o conteúdo. Os links NÃO
                crescem em 2xl: o max-w-7xl não alarga, e com px-16 a linha fica mais
                estreita (1152px) do que em xl — texto maior deitava a navbar para o
                menu hambúrguer precisamente nos ecrãs mais largos. */}
            <div className="max-w-7xl mx-auto px-8 lg:px-16 xl:px-8 2xl:px-16">
                <div ref={rowRef} className="flex justify-between items-start h-16 lg:h-20">
                    {/* Logo — grande a transbordar da barra; ao fazer scroll passa ao
                        símbolo compacto (favicon), que cabe dentro da barra */}
                    <div className={`flex-shrink-0 flex h-16 lg:h-20 ${scrolled ? 'items-center' : 'items-start'}`}>
                        <Link
                            to="/"
                            className="hover:opacity-90 transition-opacity block focus-visible:ring-2 focus-visible:ring-brand-600 rounded-lg"
                            aria-label={t('navbar.logoAria')}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.img
                                    key={scrolled ? 'compacto' : 'completo'}
                                    src={scrolled ? logoCompacto : '/assets/images/logo-pessoasestrangeiras.svg'}
                                    alt={t('navbar.logoAlt')}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.85 }}
                                    transition={transitionProps(0.18)}
                                    className={
                                        scrolled
                                            ? 'h-11 lg:h-12 w-auto object-contain'
                                            : 'h-24 lg:h-28 w-auto object-contain'
                                    }
                                />
                            </AnimatePresence>
                        </Link>
                    </div>

                    {/* Desktop Navigation — só a partir de xl: com 7 links + pílula +
                        seletor de língua, e rótulos mais longos noutras línguas, a
                        1024px a barra quebrava em duas linhas */}
                    <div
                        className={`hidden xl:flex items-center h-16 lg:h-20 ${
                            naoCabe ? 'xl:invisible xl:absolute xl:end-0 xl:pointer-events-none' : ''
                        }`}
                        aria-hidden={naoCabe || undefined}
                    >
                        <div ref={linksRef} className="ms-6 flex items-center gap-x-3">
                            {navLinks.map((link) =>
                                link.pill ? (
                                    /* Oval "Apoio" button */
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        className={({ isActive }) =>
                                            `px-4 py-1.5 text-[13px] font-bold transition-all whitespace-nowrap rounded-full border-2 border-brand-600 ${
                                                isActive
                                                    ? 'bg-brand-600 text-white'
                                                    : 'bg-white text-slate-700 hover:text-brand-600'
                                            }`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                ) : (
                                    /* Regular nav link */
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        end={link.href === '/'}
                                        className={({ isActive }) =>
                                            `px-1 py-2 text-[13px] font-bold transition-all whitespace-nowrap border-b-2 ${
                                                isActive
                                                    ? 'text-brand-600 border-brand-600'
                                                    : 'text-slate-700 border-transparent hover:text-brand-600 hover:border-brand-600'
                                            }`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                )
                            )}
                            <LanguageSelector />
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className={`${naoCabe ? '' : 'xl:hidden '}flex items-center h-16 lg:h-20`}>
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="p-2 rounded-md text-slate-700 focus-visible:ring-2 focus-visible:ring-brand-600"
                            aria-label={isOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
                            aria-expanded={isOpen}
                            aria-controls="menu-mobile"
                        >
                            {isOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        id="menu-mobile"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={transitionProps(0.3)}
                        className={`${naoCabe ? '' : 'xl:hidden '}glass border-t border-slate-100 overflow-hidden bg-white`}
                    >
                        <div className="px-4 pt-2 pb-6 space-y-1 max-h-[80vh] overflow-y-auto">
                            {navLinks.map((link) =>
                                link.pill ? (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        className={({ isActive }) =>
                                            `block mx-4 my-2 px-4 py-3 text-base font-bold rounded-full border-2 border-brand-600 text-center transition-colors ${
                                                isActive
                                                    ? 'bg-brand-600 text-white'
                                                    : 'bg-white text-slate-700'
                                            }`
                                        }
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {link.name}
                                    </NavLink>
                                ) : (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        end={link.href === '/'}
                                        /* Página atual: barra lateral + fundo da marca, como o
                                            sublinhado no desktop — só a cor do texto não chegava */
                                        className={({ isActive }) =>
                                            `block px-4 py-4 text-lg font-bold border-b border-slate-100 border-s-4 transition-colors ${
                                                isActive
                                                    ? 'text-brand-600 bg-brand-50 border-s-brand-600'
                                                    : 'text-slate-800 border-s-transparent active:bg-slate-50'
                                            }`
                                        }
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {link.name}
                                    </NavLink>
                                )
                            )}
                            <div className="pt-3 border-t border-slate-100 mt-3">
                                <LanguageSelector variant="inline" />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    )
}
